import { jsx, jsxs } from "react/jsx-runtime";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  __css,
  mergeClasses,
  tokens
} from "@fluentui/react-components";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
const useStyles = __css({"body":{"Dbcxam":0,"rjzwhg":0,"Bblux5w":"f18qryls"},"destructive":{"sj55zd":"f1ca9wz","g2u3we":"f1f8dvr7","B9xav0g":"fo25q1j","ijz38e":"fu2xbgv","Ctt3hk":"fceeiu4","Bi91k9c":"f1c66vwv","Jwef8y":"f1usghc4","Bgoe8wy":"fl0aspu","oetu4i":"f1p1a7ts","Br2s7ns":"f16ooxjl","B8u4isl":"f1e73890"}});
function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onError
}) {
  const { t } = useTranslation("ui");
  const styles = useStyles();
  const [busy, setBusy] = useState(false);
  const returnFocus = useRef(null);
  useEffect(() => {
    if (open) return void 0;
    const outsideDialogs = (el) => el instanceof HTMLElement && el !== document.body && !el.closest('[role="dialog"], [role="alertdialog"]');
    const remember = (event) => {
      if (outsideDialogs(event.target))
        returnFocus.current = event.target;
    };
    const opener = returnFocus.current;
    if (!opener && outsideDialogs(document.activeElement))
      returnFocus.current = document.activeElement;
    document.addEventListener("focusin", remember);
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      const active = document.activeElement;
      const target = opener;
      const outOfDialog = !active || active === document.body || outsideDialogs(active);
      if (outOfDialog) {
        if (target?.isConnected && active !== target) target.focus();
        window.clearInterval(timer);
      } else if (tries >= 20) window.clearInterval(timer);
    }, 50);
    return () => {
      document.removeEventListener("focusin", remember);
      window.clearInterval(timer);
    };
  }, [open]);
  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      onError?.(error);
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      open,
      modalType: "modal",
      onOpenChange: (_event, data) => {
        if (!data.open && busy) return;
        onOpenChange(data.open);
      },
      children: /* @__PURE__ */ jsx(DialogSurface, { children: /* @__PURE__ */ jsxs(DialogBody, { children: [
        /* @__PURE__ */ jsx(DialogTitle, { children: title }),
        body ? /* @__PURE__ */ jsx(DialogContent, { children: /* @__PURE__ */ jsx("div", { className: styles.body, children: body }) }) : null,
        /* @__PURE__ */ jsxs(DialogActions, { children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              appearance: "secondary",
              disabledFocusable: busy,
              autoFocus: destructive,
              onClick: () => {
                if (!busy) onOpenChange(false);
              },
              children: cancelLabel ?? t("components.confirmDialog.cancel")
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              appearance: destructive ? "secondary" : "primary",
              className: mergeClasses(destructive && styles.destructive),
              disabledFocusable: busy,
              autoFocus: !destructive,
              "aria-busy": busy,
              ...busy ? { icon: /* @__PURE__ */ jsx(Spinner, { size: "tiny", "aria-label": t("components.confirmDialog.busy") }) } : {},
              "data-destructive": destructive ? "true" : void 0,
              onClick: () => {
                if (!busy) void handleConfirm();
              },
              children: confirmLabel ?? t("components.confirmDialog.confirm")
            }
          )
        ] })
      ] }) })
    }
  );
}
export {
  ConfirmDialog
};
//# sourceMappingURL=confirm-dialog.js.map

import "/opt/batiflow/app/packages/ui/dist/components/confirm-dialog/confirm-dialog.griffel.css";